// Self-service recovery for clients whose cached state has become unusable.
//
// A browser can end up holding an IndexedDB cache that no longer matches the
// running app, at which point the normal version check cannot repair it. Rather
// than walking someone through devtools, send them a link ending in ?reset=1
// and this clears the app's own storage before React mounts.
//
// Firebase keeps its auth session in its own IndexedDB database, which is
// deliberately left alone so the user stays signed in through the reset.

const RESET_PARAM = 'reset';
// Clearing storage does not touch the HTTP cache, so the page we land on after
// a reset could still be served from a stale copy of index.html. A unique
// parameter forces that request to miss the cache.
const CACHE_BUST_PARAM = '_r';
const APP_DATABASES = ['schoolDataCache', 'districtDataCache'];
const DELETE_TIMEOUT = 3000;

// Always resolves. A blocked deletion emits no further event, and a reset that
// hangs would be worse than one that is incomplete.
function deleteDatabase(name) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    setTimeout(done, DELETE_TIMEOUT);

    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = done;
      request.onerror = done;
      request.onblocked = done;
    } catch (e) {
      done();
    }
  });
}

// Tidies the address bar on the load that follows a reset. Uses replaceState so
// it costs no navigation, and runs before the router mounts.
function stripCacheBustParam(params) {
  if (!params.has(CACHE_BUST_PARAM)) return;

  try {
    params.delete(CACHE_BUST_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  } catch (e) {
    // Cosmetic only; a leftover parameter does not affect the app.
  }
}

/**
 * Clears cached app state when the URL carries ?reset=1, then navigates to a
 * cache-busted URL without that parameter, so a refresh cannot trigger a second
 * reset and the reloaded page cannot come from a stale cache entry.
 *
 * @returns {Promise<boolean>} True when a reset ran and navigation is underway,
 *   in which case the caller should skip rendering.
 */
export async function maybeResetAppState() {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return false;
  }

  if (params.get(RESET_PARAM) !== '1') {
    stripCacheBustParam(params);
    return false;
  }

  // Reuse the update overlay so the wait looks intentional rather than broken.
  try {
    document.documentElement.classList.add('show-version-overlay');
  } catch (e) {
    // Non-fatal; the reset still runs without the overlay.
  }

  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {
    console.warn('Storage clear failed during reset:', e);
  }

  await Promise.all(APP_DATABASES.map(deleteDatabase));

  params.delete(RESET_PARAM);
  params.set(CACHE_BUST_PARAM, Date.now().toString(36));
  window.location.replace(
    `${window.location.pathname}?${params.toString()}${window.location.hash}`
  );

  return true;
}
